import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogCancelButton,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { Textarea } from '@/components/ui-kit/textarea';
import { Label } from '@/components/ui-kit/label';
import { useBulkReviewAttendanceCorrections } from '../../hooks/use-manage-attendance';

export interface BulkReviewDialogProps {
  /** The requests to decide; the dialog is open while this holds any. */
  itemIds: string[];
  action: 'Approved' | 'Rejected';
  onClose: (reviewed: boolean) => void;
}

/**
 * Approves or rejects the selected requests, each through its own workflow run (every request is
 * re-checked on its own). A rejection always carries a note, as a single rejection does.
 */
export const BulkReviewDialog = ({ itemIds, action, onClose }: BulkReviewDialogProps) => {
  const { t } = useTranslation();
  const [note, setNote] = useState('');
  const { mutateAsync: bulkReview, isPending } = useBulkReviewAttendanceCorrections();
  const isReject = action === 'Rejected';

  useEffect(() => setNote(''), [itemIds, action]);

  const handleConfirm = async () => {
    try {
      await bulkReview({
        itemIds,
        status: action,
        note: isReject ? note.trim() || undefined : undefined,
      });
      onClose(true);
    } catch {
      // Error toast is already shown by the mutation's global error handler.
    }
  };

  return (
    <Dialog
      open={itemIds.length > 0}
      onOpenChange={(open) => !open && !isPending && onClose(false)}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t(isReject ? 'BULK_REJECT' : 'BULK_APPROVE')}</DialogTitle>
          <DialogDescription>
            {t(isReject ? 'BULK_REJECT_DESCRIPTION' : 'BULK_APPROVE_DESCRIPTION', {
              count: itemIds.length,
            })}
          </DialogDescription>
        </DialogHeader>
        {/* Only a rejection takes a note (the reject workflow requires one). */}
        {isReject && (
          <div className="space-y-1.5">
            <Label htmlFor="bulk-review-note">{t('REJECTION_NOTE')}</Label>
            <Textarea
              id="bulk-review-note"
              rows={3}
              placeholder={t('REJECTION_NOTE')}
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          </div>
        )}
        <DialogFooter>
          <DialogCancelButton onClick={() => onClose(false)} disabled={isPending}>
            {t('CANCEL')}
          </DialogCancelButton>
          <Button
            type="button"
            variant={isReject ? 'destructive' : 'default'}
            onClick={handleConfirm}
            loading={isPending}
            disabled={isPending || (isReject && !note.trim())}
          >
            {t(isReject ? 'REJECT' : 'APPROVE')} ({itemIds.length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
